import React from 'react';
import { Box } from '@mui/material';
import { formatMarketNumber } from '../marketComparison';
import { useMarketTone } from '../marketTone';

const W = 640;
const H = 170;
const PAD = { left: 48, right: 16, top: 14, bottom: 30 };

/** Tramos seguidos de puntos con dato (la línea nunca cruza un hueco). */
export function trendSegments(points) {
  const segments = [];
  let current = [];
  points.forEach((p, i) => {
    if (p.median === null) {
      if (current.length) segments.push(current);
      current = [];
    } else {
      current.push(i);
    }
  });
  if (current.length) segments.push(current);
  return segments;
}

/** Tramos seguidos de períodos sin datos (franjas «sin datos»). */
export function trendGaps(points) {
  const gaps = [];
  let start = null;
  points.forEach((p, i) => {
    if (p.median === null && start === null) start = i;
    if (p.median !== null && start !== null) {
      gaps.push([start, i - 1]);
      start = null;
    }
  });
  if (start !== null) gaps.push([start, points.length - 1]);
  return gaps;
}

/**
 * «Evolución» en SVG a mano (sin librerías): mediana por período del mercado general. La línea
 * se corta en los huecos (`median: null`) y cada hueco lleva una franja «sin datos». Etiquetas
 * del eje: `points[].label` de la API.
 *
 * @param {{ trend: { points: Array<{ label: string, median: number|null, n: number }> } }} props
 */
export default function MarketTrendChart({ trend }) {
  const tone = useMarketTone();
  const points = trend?.points || [];
  if (points.length < 2) return null;
  const values = points.map((p) => p.median).filter((v) => v !== null);
  const vMin = Math.min(...values);
  const vMax = Math.max(...values);
  const span = vMax - vMin || Math.abs(vMax) * 0.1 || 1;
  const yMin = vMin - span * 0.15;
  const yMax = vMax + span * 0.15;
  const plotW = W - PAD.left - PAD.right;
  const plotH = H - PAD.top - PAD.bottom;
  const step = plotW / (points.length - 1);
  const x = (i) => PAD.left + i * step;
  const y = (v) => PAD.top + (1 - (v - yMin) / (yMax - yMin)) * plotH;
  const ticks = [yMax, (yMax + yMin) / 2, yMin];
  const labelEvery = Math.ceil(points.length / 8);

  return (
    <Box
      component="svg"
      viewBox={`0 0 ${W} ${H}`}
      role="img"
      aria-label="Evolución de la mediana por período"
      data-testid="market-trend-chart"
      sx={{ width: '100%', height: 'auto', display: 'block' }}
    >
      {ticks.map((t) => (
        <g key={`t${t}`}>
          <line x1={PAD.left} x2={W - PAD.right} y1={y(t)} y2={y(t)} stroke={tone.chartGrid} />
          <text x={PAD.left - 6} y={y(t) + 3} fill={tone.chartAxis} fontSize="10" textAnchor="end">{formatMarketNumber(t)}</text>
        </g>
      ))}
      {trendGaps(points).map(([a, b]) => {
        const x0 = Math.max(PAD.left, x(a) - step / 2);
        const x1 = Math.min(W - PAD.right, x(b) + step / 2);
        return (
          <g key={`gap${a}`} data-testid="market-trend-gap">
            <rect x={x0} y={PAD.top} width={Math.max(0, x1 - x0)} height={plotH} fill={tone.chartGap} />
            {x1 - x0 >= 44 ? (
              <text x={(x0 + x1) / 2} y={PAD.top + plotH / 2 + 3} fill={tone.chartAxis} fontSize="10" textAnchor="middle">sin datos</text>
            ) : null}
          </g>
        );
      })}
      {trendSegments(points).map((seg) => (
        seg.length > 1 ? (
          <polyline
            key={`seg${seg[0]}`}
            data-testid="market-trend-line"
            points={seg.map((i) => `${x(i)},${y(points[i].median)}`).join(' ')}
            fill="none"
            stroke={tone.chartLine}
            strokeWidth="2.5"
            strokeLinejoin="round"
          />
        ) : null
      ))}
      {points.map((p, i) => (p.median !== null ? (
        <circle key={`pt${p.label}`} cx={x(i)} cy={y(p.median)} r="3.5" fill={tone.chartLine}>
          <title>{`${p.label}: ${formatMarketNumber(p.median)} (${p.n})`}</title>
        </circle>
      ) : null))}
      {points.map((p, i) => ((points.length - 1 - i) % labelEvery === 0 ? (
        <text key={`lb${p.label}`} x={x(i)} y={H - 10} fill={tone.chartLabel} fontSize="10" textAnchor="middle">{p.label}</text>
      ) : null))}
    </Box>
  );
}
